import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from '../../components/blocks/localization';
import { FilterTag } from '../../components/composites';
import { ResellerItem, Location, DateFilterField } from '../../redux/types';
import { toFamilyTranslationKey } from '../../utils/textUtils';

interface FilterState {
    poIds: string[];
    unitsStatusFilter: string[];
    asinFilter: string[];
    resellerSkuFilter: string[];
    familyFilter: string[];
    dateRange: string[];
    dateFilterField: DateFilterField;
    shipToLocationFilter: string[];
}

interface UsePurchaseOrderFiltersProps {
    initialState: FilterState;
    resellerItemsData: ResellerItem[];
    resellerLocations?: Location[];
    onFilterChange: (filters: FilterState) => void;
}

export const usePurchaseOrderFilters = ({
    initialState,
    resellerItemsData,
    resellerLocations = [],
    onFilterChange,
}: UsePurchaseOrderFiltersProps) => {
    const { t } = useTranslation(['purchaseOrderListPage']);
    const [localPoIds, setLocalPoIds] = useState<string[]>(initialState.poIds);
    const [localUnitsStatusFilter, setLocalUnitsStatusFilter] = useState<string[]>(initialState.unitsStatusFilter);
    const [localAsinFilter, setLocalAsinFilter] = useState<string[]>(initialState.asinFilter);
    const [localResellerSkuFilter, setLocalResellerSkuFilter] = useState<string[]>(initialState.resellerSkuFilter);
    const [localFamilyFilter, setLocalFamilyFilter] = useState<string[]>(initialState.familyFilter);
    const [localDateRange, setLocalDateRange] = useState<string[]>(initialState.dateRange);
    const [localDateFilterField, setLocalDateFilterField] = useState<DateFilterField>(
        initialState.dateFilterField || 'creationDate'
    );
    const [localShipToLocationFilter, setLocalShipToLocationFilter] = useState<string[]>(
        initialState.shipToLocationFilter || []
    );

    const filterTags: FilterTag[] = useMemo(
        () => [
            ...localPoIds.map((v) => ({
                label: t('filterTag-poId', { value: v }),
                key: `po-${v}`,
                remove: () => setLocalPoIds((f) => f.filter((x) => x !== v)),
            })),
            ...localUnitsStatusFilter.map((v) => ({
                label: t('filterTag-status', { value: v }),
                key: `status-${v}`,
                remove: () => setLocalUnitsStatusFilter((f) => f.filter((x) => x !== v)),
            })),
            ...localAsinFilter.map((v) => ({
                label: t('filterTag-asin', { value: v }),
                key: `asin-${v}`,
                remove: () => setLocalAsinFilter((f) => f.filter((x) => x !== v)),
            })),
            ...localResellerSkuFilter.map((v) => ({
                label: t('filterTag-sku', { value: v }),
                key: `sku-${v}`,
                remove: () => setLocalResellerSkuFilter((f) => f.filter((x) => x !== v)),
            })),
            ...localFamilyFilter.map((v) => ({
                label: t('filterTag-family', {
                    value: t(toFamilyTranslationKey(v), { defaultValue: v }),
                }),
                key: `family-${v}`,
                remove: () => setLocalFamilyFilter((f) => f.filter((x) => x !== v)),
            })),
            ...localShipToLocationFilter.map((v) => ({
                label: t('filterTag-location', { value: v }),
                key: `location-${v}`,
                remove: () => setLocalShipToLocationFilter((f) => f.filter((x) => x !== v)),
            })),
        ],
        [
            localPoIds,
            localUnitsStatusFilter,
            localAsinFilter,
            localResellerSkuFilter,
            localFamilyFilter,
            localShipToLocationFilter,
            t,
        ]
    );

    const extractUnique = useMemo(
        () => (key: keyof ResellerItem) =>
            [...new Set(resellerItemsData.map((item) => item[key]).filter(Boolean))] as string[],
        [resellerItemsData]
    );

    const asinOptions = useMemo(() => extractUnique('asin'), [extractUnique]);
    const familyOptions = useMemo(() => extractUnique('family'), [extractUnique]);
    const resellerSkuOptions = useMemo(() => extractUnique('externalSku'), [extractUnique]);
    const shipToLocationOptions = useMemo(
        () => resellerLocations.filter((l) => l.status === 'Active' && l.shippingAddress).map((l) => l.code),
        [resellerLocations]
    );

    const submitQuery = useCallback(() => {
        onFilterChange({
            poIds: localPoIds,
            unitsStatusFilter: localUnitsStatusFilter,
            asinFilter: localAsinFilter,
            resellerSkuFilter: localResellerSkuFilter,
            familyFilter: localFamilyFilter,
            dateRange: localDateRange,
            dateFilterField: localDateFilterField,
            shipToLocationFilter: localShipToLocationFilter,
        });
    }, [
        onFilterChange,
        localPoIds,
        localUnitsStatusFilter,
        localAsinFilter,
        localResellerSkuFilter,
        localFamilyFilter,
        localDateRange,
        localDateFilterField,
        localShipToLocationFilter,
    ]);

    const clearFilters = useCallback(() => {
        setLocalPoIds([]);
        setLocalDateRange([]);
        setLocalDateFilterField('creationDate');
        setLocalUnitsStatusFilter([]);
        setLocalAsinFilter([]);
        setLocalFamilyFilter([]);
        setLocalResellerSkuFilter([]);
        setLocalShipToLocationFilter([]);
    }, []);

    return {
        filters: {
            poIds: { value: localPoIds, onChange: setLocalPoIds },
            unitsStatus: { value: localUnitsStatusFilter, onChange: setLocalUnitsStatusFilter },
            asin: { value: localAsinFilter, onChange: setLocalAsinFilter },
            resellerSku: { value: localResellerSkuFilter, onChange: setLocalResellerSkuFilter },
            family: { value: localFamilyFilter, onChange: setLocalFamilyFilter },
            dateRange: { value: localDateRange, onChange: setLocalDateRange },
            dateFilterField: { value: localDateFilterField, onChange: setLocalDateFilterField },
            shipToLocation: { value: localShipToLocationFilter, onChange: setLocalShipToLocationFilter },
        },
        options: { asinOptions, familyOptions, resellerSkuOptions, shipToLocationOptions },
        filterTags,
        submitQuery,
        clearFilters,
    };
};
